import { useEffect, useRef, useState } from 'react'

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Shared dismissal behavior for popovers/menus: click outside or Escape closes.
 * Attach the returned ref to the wrapper that contains both trigger and panel —
 * every dropdown in the app gets identical, accessible dismiss semantics.
 */
export function useDismissable<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])
  return ref
}

/**
 * Scroll parallax: translates the element vertically against scroll, proportional to its
 * distance from the viewport center. speed 0.1–0.4 feels editorial; negative inverts.
 * rAF-driven transform — no layout thrash, disabled under reduced motion.
 */
export function useParallax<T extends HTMLElement>(speed = 0.2) {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || prefersReducedMotion()) return
    let raf = 0
    let ticking = false
    const update = () => {
      ticking = false
      const r = el.getBoundingClientRect()
      const mid = r.top + r.height / 2 - window.innerHeight / 2
      el.style.transform = `translate3d(0, ${(-mid * speed).toFixed(1)}px, 0)`
    }
    const onScroll = () => {
      if (!ticking) {
        ticking = true
        raf = requestAnimationFrame(update)
      }
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [speed])
  return ref
}

/** Count up to `target` once the element scrolls into view (~1s ease-out). */
export function useCounter(target: number, inView: boolean, duration = 1100) {
  const [value, setValue] = useState(0)
  useEffect(() => {
    if (!inView) return
    if (prefersReducedMotion()) {
      setValue(target)
      return
    }
    let raf = 0
    const t0 = performance.now()
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / duration)
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, inView, duration])
  return value
}

/**
 * Drive an element's inline style straight from scrollY — rAF-throttled DOM writes,
 * zero React re-renders (the setState-per-frame version re-rendered the whole hero
 * subtree on every scroll frame). `apply` is captured once on mount.
 */
export function useScrollStyle<T extends HTMLElement>(apply: (el: T, y: number) => void) {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || prefersReducedMotion()) return
    let raf = 0
    let ticking = false
    const update = () => {
      ticking = false
      if (ref.current) apply(ref.current, window.scrollY)
    }
    const onScroll = () => {
      if (!ticking) {
        ticking = true
        raf = requestAnimationFrame(update)
      }
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return ref
}
